import React from 'react';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { useAvailableActions } from '../../../hooks/useAvailableActions';
import { SkillkoFieldBadge } from '../profile-form/components';

const CreateProfileHeader = ({ 
  openPicturePopup, 
  imageSrc, 
  profileData, 
  isEditProfile, 
  newProfileText='New Profile', 
  error, 
  isDeletedProfile = false,
  onSavePicture,
  isPictureEdited = false,
  canEdit = true,
  isSkillkoManaged = false,
}) => {
  const theme = useTheme();
  const { canPerformProfileAdminActions } = useAvailableActions();
  const name = `${profileData.firstName} ${profileData.surname}`;
  const isNameNotEmpty = profileData.firstName || profileData.surname;

  const handleDownloadPicture = () => {
    if (!imageSrc) return;
    
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = imageSrc;
    
    // Generate filename from profile name or use default
    const fileName = isNameNotEmpty 
      ? `${profileData.firstName}_${profileData.surname}_profile_picture.png`.replace(/\s+/g, '_')
      : 'profile_picture.png';
    
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box marginBottom={{ xs: 3, sm: 5 }}>
      <Grid 
        container 
        alignItems="center" 
        spacing={{ xs: 2, sm: 3 }} 
        sx={{
          flexDirection: { xs: 'column', sm: 'row' },
          textAlign: { xs: 'center', sm: 'left' }
        }}
      >
        <Grid 
          item 
          marginLeft={{ xs: 0, sm: 3 }}
          data-profile-field="image"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Avatar
              src={imageSrc}
              sx={{
                width: { xs: 60, sm: 73 },
                height: { xs: 60, sm: 73 },
                margin: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.300',
              }}
            >
              {!!!imageSrc && (
                <Icon
                  icon="fa6-solid:user"
                  width="28"
                  height="28"
                  color={theme.palette.text.secondary}
                />
              )}
            </Avatar>
            {isSkillkoManaged && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  backgroundColor: 'background.paper',
                  borderRadius: '50%',
                  boxShadow: 1,
                  p: 0.25,
                  display: 'flex',
                }}
              >
                <SkillkoFieldBadge />
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
            {!isDeletedProfile && canEdit && (
              <Button 
                variant="text" 
                size="small" 
                sx={{ fontSize: { xs: '11px', sm: '12px' } }} 
                onClick={openPicturePopup}
              >
                {isEditProfile ? 'EDIT PICTURE' : 'ADD PICTURE'}
              </Button>
            )}
            {imageSrc && canPerformProfileAdminActions && (
              <Button 
                variant="text" 
                size="small" 
                sx={{ fontSize: { xs: '11px', sm: '12px' } }} 
                onClick={handleDownloadPicture}
                startIcon={<Icon icon="mdi:download" width="14" height="14" />}
              >
                DOWNLOAD
              </Button>
            )}
          </Box>
          {error && (
            <Typography 
              variant="caption" 
              color="error.main" 
              sx={{ display: 'block', textAlign: 'center', mt: 1 }}
            >
              {error}
            </Typography>
          )}
        </Grid>

        <Grid 
          item 
          sx={{ 
            ml: { xs: 0, sm: 2 },
            flexGrow: 1,
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Typography 
            variant="h4"
            data-testid="profile-name"
            sx={{ 
              fontSize: { xs: '1.5rem', sm: '2rem' }
            }}
          >
            {isNameNotEmpty ? name : newProfileText}
          </Typography>
          {isEditProfile && (
            <Typography 
              variant="body1" 
              color="textSecondary"
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              {profileData.email && <>Email: {profileData.email}</>}
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Save Picture Changes Button - positioned below the header row, aligned right like other sections */}
      {isEditProfile && isPictureEdited && canEdit && (
        <Box 
          sx={{ 
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 2
          }}
        >
          <Button 
            variant="contained" 
            color="primary"
            onClick={onSavePicture}
            sx={{ 
              minWidth: { xs: '120px', sm: '150px' }, 
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            Save Changes
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CreateProfileHeader;
